import React from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PerplexityNavigationButtonsProps {
  hasPrevious?: boolean;
  hasNext?: boolean;
  isLoadingMore?: boolean;
  hasNextPage?: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
  onLoadMore?: () => void;
}

export const PerplexityNavigationButtons: React.FC<PerplexityNavigationButtonsProps> = ({
  hasPrevious = false,
  hasNext = false,
  isLoadingMore = false,
  hasNextPage = false,
  onPrevious,
  onNext,
  onLoadMore
}) => {
  // Optimized next button handler
  const handleNextClick = () => {
    if (hasNext && onNext && !isLoadingMore) {
      onNext();
    } else if (hasNextPage && !isLoadingMore && onLoadMore) {
      onLoadMore();
    }
  };

  const canClickNext = (hasNext || hasNextPage) && !isLoadingMore;

  return (
    <div className="flex justify-between items-center gap-3 w-full">
      <motion.button
        className={cn(
          "flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all flex-1 backdrop-blur-sm border",
          hasPrevious
            ? "bg-gradient-to-r from-slate-100/80 to-gray-100/80 dark:from-slate-800/80 dark:to-gray-800/80 border-slate-200/50 dark:border-slate-700/50 text-slate-700 dark:text-slate-200 hover:from-slate-200/90 hover:to-gray-200/90 dark:hover:from-slate-700/90 dark:hover:to-gray-700/90 shadow-sm hover:shadow-md"
            : "bg-slate-50/60 dark:bg-slate-900/60 border-slate-200/30 dark:border-slate-800/30 text-slate-400 dark:text-slate-600 cursor-not-allowed"
        )}
        onClick={hasPrevious && onPrevious ? onPrevious : undefined}
        disabled={!hasPrevious}
        whileHover={hasPrevious ? { scale: 1.02, y: -1 } : {}}
        whileTap={hasPrevious ? { scale: 0.98 } : {}}
        data-test-id="research-prev-button"
      >
        <ChevronLeft size={16} className="flex-shrink-0" />
        <span>Last</span>
      </motion.button>
      
      <motion.button
        className={cn(
          "flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all flex-1 backdrop-blur-sm border",
          canClickNext
            ? "bg-gradient-to-r from-slate-800 to-slate-900 dark:from-slate-700 dark:to-slate-800 border-slate-700/50 dark:border-slate-600/50 text-white hover:from-slate-700 hover:to-slate-800 dark:hover:from-slate-600 dark:hover:to-slate-700 shadow-sm hover:shadow-md"
            : "bg-slate-50/60 dark:bg-slate-900/60 border-slate-200/30 dark:border-slate-800/30 text-slate-400 dark:text-slate-600 cursor-not-allowed"
        )}
        onClick={canClickNext ? handleNextClick : undefined}
        disabled={!canClickNext}
        whileHover={canClickNext && !isLoadingMore ? { scale: 1.02, y: -1 } : {}}
        whileTap={canClickNext && !isLoadingMore ? { scale: 0.98 } : {}}
        data-test-id="research-next-button"
        type="button"
      >
        {isLoadingMore ? (
          <span className="text-sm font-medium bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent animate-pulse">
            Loading...
          </span>
        ) : (
          <>
            <span>Next</span>
            <ChevronRight size={16} className="flex-shrink-0" />
          </>
        )}
      </motion.button>
    </div>
  );
}; 